/**
 * MarketDataDatasource
 *
 * TradingView datafeed implementation that fetches live market data
 * from market-data-api via the BFF proxy. Supports multiple resolutions,
 * range-based caching, scroll-to-load, and resolution switching.
 *
 * start/end from the <asset> tag are viewport hints only — they control
 * the initial visible range via setVisibleRange, not data boundaries.
 * The datasource always fetches freely in both directions.
 */

import type {
  IDatafeedChartApi,
  IExternalDatafeed,
  LibrarySymbolInfo,
  ResolutionString,
  OnReadyCallback,
  SearchSymbolsCallback,
  ResolveCallback,
  DatafeedErrorCallback,
  HistoryCallback,
  PeriodParams,
  SubscribeBarsCallback,
} from '../../public/charting_library/datafeed-api';

import { Bar, fetchBarsForRange, getNominalWindow } from './market-data-utils';

// Resolution string → interval mapping
const RESOLUTION_MAP: Record<string, string> = {
  '5': '5m',
  '60': '1h',
  '1D': '1d',
};

// Per-asset-type symbol config
interface AssetConfig {
  type: string;
  exchange: string;
  timezone: string;
  session: string;
  pricescale: number;
}

const ASSET_CONFIGS: Record<string, AssetConfig> = {
  stock:     { type: 'stock',     exchange: 'NYSE',   timezone: 'America/New_York', session: '0930-1600', pricescale: 10000 },
  commodity: { type: 'commodity', exchange: 'COMEX',   timezone: 'America/New_York', session: '1800-1700', pricescale: 10000 },
  crypto:    { type: 'crypto',    exchange: 'CRYPTO',  timezone: 'Etc/UTC',          session: '24x7',      pricescale: 10000 },
  forex:     { type: 'forex',     exchange: 'FOREX',   timezone: 'Etc/UTC',          session: '1700-1700',  pricescale: 100000 },
};

interface DatasourceConfig {
  symbol: string;
  assetType: string;
  interval: string;
}

// Minimum distinct advancing empty probes before span-based exhaustion can fire.
// Prevents a single large gap from exhausting immediately.
const MIN_EMPTY_PROBES_FOR_EXHAUSTION = 2;

/**
 * Merge bars into a sorted, deduplicated array.
 * Keeps the bar from the latest fetch on timestamp collision.
 */
function mergeBars(existing: Bar[], incoming: Bar[]): Bar[] {
  const map = new Map<number, Bar>();

  for (const bar of existing) {
    map.set(bar.time, bar);
  }

  for (const bar of incoming) {
    const prev = map.get(bar.time);
    if (prev && (prev.open !== bar.open || prev.high !== bar.high || prev.low !== bar.low || prev.close !== bar.close)) {
      console.warn(`[MarketDataDatasource] OHLCV conflict at ${bar.time}: replacing cached bar`);
    }
    map.set(bar.time, bar); // latest fetch wins
  }

  const merged = Array.from(map.values());
  merged.sort((a, b) => a.time - b.time);
  return merged;
}

export class MarketDataDatasource implements IDatafeedChartApi, IExternalDatafeed {
  private config: DatasourceConfig;
  private cache: Map<string, Bar[]> = new Map(); // keyed by interval
  // Exhaustion = "no older history before current cache minimum"
  private backwardExhausted: Map<string, boolean> = new Map();
  // Count of distinct advancing empty backward probes (each must extend the frontier)
  private emptyProbeCount: Map<string, number> = new Map();
  // Lowest timestamp (seconds) we've probed empty — deduplicates repeated requests
  // over the same gap and tracks the upper bound of cumulative empty span.
  private emptyProbeLowWaterMark: Map<string, number> = new Map();
  // Upper edge of the first empty backward probe (cacheMin when emptiness started).
  // emptySpan = emptyProbeHighMark - emptyProbeLowWaterMark.
  private emptyProbeHighMark: Map<string, number> = new Map();

  constructor(config: DatasourceConfig) {
    this.config = config;
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(() => {
      callback({
        supported_resolutions: ['5', '60', '1D'] as ResolutionString[],
        exchanges: [],
        symbols_types: [],
        supports_time: false,
        supports_marks: false,
        supports_timescale_marks: false,
      });
    }, 0);
  }

  searchSymbols(
    _userInput: string,
    _exchange: string,
    _symbolType: string,
    onResult: SearchSymbolsCallback
  ): void {
    onResult([]);
  }

  resolveSymbol(
    symbolName: string,
    onResolve: ResolveCallback,
    onError: DatafeedErrorCallback
  ): void {
    const assetConfig = ASSET_CONFIGS[this.config.assetType];
    if (!assetConfig) {
      setTimeout(() => onError(`Unknown asset type: ${this.config.assetType}`), 0);
      return;
    }

    const symbolInfo: LibrarySymbolInfo = {
      ticker: this.config.symbol,
      name: symbolName,
      description: this.config.symbol,
      type: assetConfig.type,
      session: assetConfig.session,
      timezone: assetConfig.timezone as LibrarySymbolInfo['timezone'],
      exchange: assetConfig.exchange,
      listed_exchange: assetConfig.exchange,
      format: 'price',
      pricescale: assetConfig.pricescale,
      minmov: 1,
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      supported_resolutions: ['5', '60', '1D'] as ResolutionString[],
    };

    // TradingView requires resolveSymbol to call back asynchronously
    setTimeout(() => onResolve(symbolInfo), 0);
  }

  /**
   * Seed the cache with externally-fetched bars (from preflight).
   */
  seedBars(interval: string, bars: Bar[]): void {
    const existing = this.cache.get(interval) || [];
    this.cache.set(interval, mergeBars(existing, bars));
  }

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    const interval = RESOLUTION_MAP[resolution as string];
    if (!interval) {
      onError(`Unsupported resolution: ${resolution}`);
      return;
    }

    const fromSec = periodParams.from;
    const toSec = periodParams.to;
    const fromMs = fromSec * 1000;
    const toMs = toSec * 1000;
    const inRange = (t: number) => t >= fromMs && t <= toMs;

    const cached = this.cache.get(interval) || [];

    // Plan fetch legs: backward and/or forward, relative to cache bounds
    let backwardFrom: number | null = null;
    let backwardTo: number | null = null;
    let forwardFrom: number | null = null;
    let forwardTo: number | null = null;

    if (cached.length === 0) {
      // No cache at all — treat the whole request as a single fetch
      backwardFrom = fromSec;
      backwardTo = toSec;
    } else {
      const cacheMinSec = cached[0].time / 1000;
      const cacheMaxSec = cached[cached.length - 1].time / 1000;

      // Backward leg: request starts before cache minimum
      if (fromSec < cacheMinSec) {
        if (!this.backwardExhausted.get(interval)) {
          backwardFrom = fromSec;
          backwardTo = cacheMinSec;
        }
      }

      // Forward leg: request extends beyond cache maximum
      if (toSec > cacheMaxSec) {
        forwardFrom = cacheMaxSec;
        forwardTo = toSec;
      }
    }

    // Execute backward fetch (with prefetch buffer)
    if (backwardFrom !== null && backwardTo !== null) {
      const rangeWidth = backwardTo - backwardFrom;
      const fetchFrom = backwardFrom - Math.floor(rangeWidth * 0.5);

      try {
        const result = await fetchBarsForRange(
          this.config.assetType,
          this.config.symbol,
          interval,
          fetchFrom,
          backwardTo
        );

        // Determine whether this backward fetch made real progress
        let backwardProgress = false;
        if (result.bars.length > 0) {
          const prevCacheMin = cached.length > 0 ? cached[0].time : Infinity;
          this.cache.set(interval, mergeBars(cached, result.bars));
          const newCacheMin = this.cache.get(interval)![0].time;

          if (newCacheMin < prevCacheMin) {
            // Cache actually expanded earlier — real progress
            backwardProgress = true;
            this.emptyProbeCount.set(interval, 0);
            this.emptyProbeLowWaterMark.delete(interval);
            this.emptyProbeHighMark.delete(interval);
          } else {
            // The API searched this range but returned nothing earlier than
            // what we already have (e.g. at the IPO boundary). This is direct
            // evidence there's no older data — mark exhausted immediately.
            this.backwardExhausted.set(interval, true);
          }
        }

        if (!backwardProgress && result.bars.length === 0) {
          // Track advancing empty probes via watermark
          const prevLow = this.emptyProbeLowWaterMark.get(interval);
          if (prevLow === undefined || fetchFrom < prevLow) {
            this.emptyProbeLowWaterMark.set(interval, fetchFrom);
            if (prevLow === undefined) {
              this.emptyProbeHighMark.set(interval, backwardTo);
            }

            const newCount = (this.emptyProbeCount.get(interval) || 0) + 1;
            this.emptyProbeCount.set(interval, newCount);

            // Exhausted when cumulative distinct empty span covers the nominal
            // window AND at least 2 distinct probes have fired.
            const highMark = this.emptyProbeHighMark.get(interval) ?? backwardTo;
            const emptySpan = highMark - fetchFrom;
            const nominalWindow = getNominalWindow(interval);
            if (emptySpan >= nominalWindow && newCount >= MIN_EMPTY_PROBES_FOR_EXHAUSTION) {
              this.backwardExhausted.set(interval, true);
            }
          }
        }
      } catch (error) {
        // Fetch failure: don't poison cache or exhaustion state
        console.error('[MarketDataDatasource] Backward fetch failed:', error);

        const slice = cached.filter(b => inRange(b.time));
        if (slice.length > 0) {
          onResult(slice, { noData: false });
          return;
        }

        onError(error instanceof Error ? error.message : 'Failed to fetch market data');
        return;
      }
    }

    // Execute forward fetch (no prefetch buffer)
    if (forwardFrom !== null && forwardTo !== null) {
      try {
        const updatedCache = this.cache.get(interval) || [];
        const result = await fetchBarsForRange(
          this.config.assetType,
          this.config.symbol,
          interval,
          forwardFrom,
          forwardTo
        );

        if (result.bars.length > 0) {
          this.cache.set(interval, mergeBars(updatedCache, result.bars));
        }
        // Forward empty fetches do NOT affect backward exhaustion
      } catch (error) {
        console.error('[MarketDataDatasource] Forward fetch failed:', error);

        const slice = (this.cache.get(interval) || []).filter(b => inRange(b.time));
        if (slice.length > 0) {
          onResult(slice, { noData: false });
          return;
        }

        onError(error instanceof Error ? error.message : 'Failed to fetch market data');
        return;
      }
    }

    // Return the requested slice from cache.
    // Only signal noData when history is truly exhausted.
    // Empty slices from session gaps (weekends, holidays) must NOT set noData,
    // otherwise TradingView permanently stops requesting older bars.
    const updated = this.cache.get(interval) || [];
    const slice = updated.filter(b => inRange(b.time));
    const noData = slice.length === 0 && (this.backwardExhausted.get(interval) || false);
    onResult(slice, { noData });
  }

  subscribeBars(
    _symbolInfo: LibrarySymbolInfo,
    _resolution: ResolutionString,
    _onTick: SubscribeBarsCallback,
    _listenerGuid: string,
    _onResetCacheNeededCallback: () => void
  ): void {
    // No-op: live data not supported
  }

  unsubscribeBars(_listenerGuid: string): void {
    // No-op
  }

  // Expose for testing
  _getCache(): Map<string, Bar[]> {
    return this.cache;
  }

  _isExhausted(interval: string): boolean {
    return this.backwardExhausted.get(interval) || false;
  }
}
